import { Modal, ModalProp } from "../../components/modal/Modal";
import { Dialog } from "@headlessui/react";
import { ModalFoterBox } from "../../components/box/Box";
import Button from "../../components/button/Button";
import { SetStateAction } from "react";
import { useDispatch } from "react-redux";
import { AlertValue, alertOpen } from "../../features/alert.slice";
import { ConstMessage } from "../../config/const";
import { PointAdjustHistory, AdjustUsers } from "../../model/PointData";
import ExcelJS from "exceljs";
import { convertDateTimeToStringCompactFormat } from "../../utility/ConvertUtily";
import { Company } from "../../model/Companies";
import { requestGetAdjustUsers } from "../../api/Point.api";

export type PointAdjustmentDataExportModalProps = {
  setLoadProgress: (value: SetStateAction<number>) => void;
  pointAdjustment: PointAdjustHistory;
  adjustUsersList: AdjustUsers[];
  company: Company | undefined;
};

export const PointAdjustmentDataExportModal = ({
  open,
  toggle,
  closeOnClickOutside,
  setLoadProgress,
  pointAdjustment,
  adjustUsersList,
  company,
}: ModalProp & PointAdjustmentDataExportModalProps) => {

  const dispatch = useDispatch();

  const col = [
    "登録日",
    "カテゴリ",
    "タイプ",
    "ポイント名",
    "有効期限",
    "ポイント発生日",
    "ポイント数",
    "備考",
    "登録者",
  ];

  const userCol = [
    "利用者番号",
    "名前",
    "グループ",
    "実行結果",
    "エラー内容",
  ];
  
  // UTCからJSTに変換
  const ninePlusTime = (time: Date) => {
    return new Date(time.setHours(time.getHours() + 9));
  }

  // Excel出力処理
  async function exportExcelHandler () {
    toggle();
    setLoadProgress(0);
    const result = await exportExcel();
    setLoadProgress(-1);
    if(!result.result){
      console.log(result.details);
      dispatch(alertOpen(AlertValue(ConstMessage.ERR_EXCEL_EXPORT, "danger")));
    }
  };

  const exportExcel = async () => {
    try{
      setLoadProgress(Math.round(1/4 * 100));
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('出力データ');

      worksheet.getColumn(1).width = 15;
      worksheet.getColumn(2).width = 20;
      worksheet.getColumn(3).width = 20;
      worksheet.getColumn(4).width = 20;
      worksheet.getColumn(5).width = 15;
      worksheet.getColumn(6).width = 15;
      worksheet.getColumn(7).width = 10;
      worksheet.getColumn(8).width = 20;
      worksheet.getColumn(9).width = 15;

      // 調整履歴の出力データ
      const headerRow = worksheet.getRow(1);
      const adjustHistoryDataRow = worksheet.getRow(2);

      const adjustHistoryData = [
        ninePlusTime(pointAdjustment.createAt),
        pointAdjustment.ptCategoryName,
        pointAdjustment.ptTypeName,
        pointAdjustment.ptName,
        pointAdjustment.expirationDate ? ninePlusTime(pointAdjustment.expirationDate) : "",
        ninePlusTime(pointAdjustment.occurrenceDate),
        pointAdjustment.grantPoints,
        pointAdjustment.note,
        pointAdjustment.createUser,
      ];
      for (let i = 1; i <= col.length; i++) {
        headerRow.getCell(i).value = col[i - 1];
        adjustHistoryDataRow.getCell(i).value = adjustHistoryData[i - 1];
      };
      setLoadProgress(Math.round(2/4 * 100));
      let page = 1;
      const pageSize = 5000;
      let hasMore = true;
      const allAdjustUsers: AdjustUsers[] = [];

      while (hasMore) {
        const result = await requestGetAdjustUsers(pointAdjustment.id, page, pageSize);
        if (!result.success) {
          console.error("調整対象者取得に失敗", result.error);
          dispatch(alertOpen(AlertValue(ConstMessage.ERR_FAILED_GET_DATA, "danger")));
          return { result: false, details: result.error };
        }

        allAdjustUsers.push(...result.adjustUsers);
        hasMore = result.adjustUsers.length === pageSize;
        page++;
        await new Promise((resolve) => setTimeout(resolve, 0));
      }

      setLoadProgress(Math.round(3 / 4 * 100));

      // 調整履歴対象者の出力データ
      const userHeaderRow = worksheet.getRow(4);
      const startRow = 5;
      for (let i = 1; i <= userCol.length; i++) {
        userHeaderRow.getCell(i).value = userCol[i - 1];
      }

      const adjustUsersSortList = allAdjustUsers.sort((a, b) => a.displayUserId.localeCompare(b.displayUserId));
      adjustUsersSortList.forEach((user, i) => {
        const row = worksheet.getRow(startRow + i);
        row.getCell(1).value = user.displayUserId;
        row.getCell(2).value = user.userName;
        row.getCell(3).value = user.groupNameArr?.join(" ") ?? "";
        row.getCell(4).value = user.executionResult ? "○" : "✕";
        row.getCell(5).value = user.errorDetail ?? "";
      });

      // ダウンロードリンク
      const a = document.createElement("a");
      const uint8Array = await workbook.xlsx.writeBuffer();
      const blob = new Blob([uint8Array], { type: "application/octet-binary" });
      a.href = window.URL.createObjectURL(blob);
      a.download = `ポイント調整情報_${company?.name}_${convertDateTimeToStringCompactFormat(new Date())}.xlsx`;
      a.click();
      a.remove();

      setLoadProgress(100);
      return {result: true, details: undefined};
    }catch(err){
      return {result: false, details: err};
    }
  };

  return (
    <>
      <Modal
        open={open}
        toggle={toggle}
        closeOnClickOutside={closeOnClickOutside}
      >
        <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all w-full max-w-screen-md">
          <div className="px-2 pb-4 sm:p-4">
            <div className="text-center sm:mt-3">
              <Dialog.Title as="h3" className="text-left text-lg font-medium leading-6 text-gray-900">
                ポイント調整情報出力
              </Dialog.Title>
            </div>
            <div className="mt-5">
              ポイント調整の情報をExcelファイルで出力します。<br />
              <span className="text-red-500">
                ※出力処理操作は、対象データによって<span className="font-semibold">作業時間が長時間になる場合があります</span>。
              </span>
            </div>
          </div>
          <ModalFoterBox>
            <Button
              className="mx-2 mt-1 w-[132px]"
              outline
              onClick={toggle}
              children={"キャンセル"}
            />
            <Button
              className="mt-1 mr-2 w-[132px]"
              onClick={exportExcelHandler}
              children={"OK"}
            />
          </ModalFoterBox>
        </Dialog.Panel>
      </Modal>
    </>
  );  
}
  